import { useEffect, useState } from "react";
import { MarkdownContent } from "../../../shared/components/MarkdownContent";
import { useI18n } from "../../../shared/i18n/i18n";
import { TranslationKey } from "../../../shared/i18n/i18n";
import { fetchLatestTermsOfUseForLanguage, fetchTermsOfUseByUuidForLanguage, TermsOfUseResponse } from "../../../shared/requests";
import { AppPopup } from "../../../shared/components/AppPopup";

type TermsOfUseModalProps = {
  isOpen: boolean;
  onClose: () => void;
  t: (key: TranslationKey) => string;
  termsOfUseUuid?: string;
};

export const TermsOfUseModal = ({ isOpen, onClose, t, termsOfUseUuid }: TermsOfUseModalProps) => {
  const { language } = useI18n();
  const [isLoading, setIsLoading] = useState(false);
  const [termsOfUse, setTermsOfUse] = useState<null | TermsOfUseResponse>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    let isCancelled = false;
    const loadTermsOfUse = async () => {
      setIsLoading(true);
      try {
        const { payload } = termsOfUseUuid
          ? await fetchTermsOfUseByUuidForLanguage(termsOfUseUuid, language)
          : await fetchLatestTermsOfUseForLanguage(language);
        if (!isCancelled) {
          setTermsOfUse(payload);
        }
      } catch {
        if (!isCancelled) {
          setTermsOfUse(null);
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };
    void loadTermsOfUse();
    return () => {
      isCancelled = true;
    };
  }, [isOpen, language, termsOfUseUuid]);

  return (
    <AppPopup
      actions={(
        <button
          className="admin-popup-action primary"
          onClick={onClose}
          type="button"
        >
          {t("admin.button.close")}
        </button>
      )}
      dismissible={false}
      isOpen={isOpen}
      onClose={onClose}
      panelClassName="admin-terms-modal-panel"
      title={t("admin.terms.modalTitle")}
    >
      <div className="admin-terms-modal-content">
        <MarkdownContent
          content={termsOfUse?.content ?? ""}
          emptyFallback={!isLoading ? <p className="admin-terms-popup-copy">{t("admin.message.termsUnavailable")}</p> : null}
        />
      </div>
    </AppPopup>
  );
};
